import React, { useState } from "react";
import { MessageSquareText, UserRound, UserRoundPlus, UsersRound, UtensilsCrossed } from "lucide-react";
import { Modal } from "@/components/Modals";

// The strip above the order on the billing PC (owner, 2026-10-24, after Petpooja's):
// table · captain · guests · note · customer. The captain is shown, not changed.
// Guests, note and customer open their editors; while the bill is printed they
// are read-only.

function Slot({ icon: Icon, label, value, onClick, disabled, testId }) {
  const body = (
    <>
      <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex flex-col leading-tight text-left">
        <span className="text-micro text-muted-foreground">{label}</span>
        <span className="text-meta font-medium text-foreground truncate">{value}</span>
      </span>
    </>
  );
  if (!onClick) {
    return <div className="flex items-center gap-1.5 px-2 py-1.5 min-w-0" data-testid={testId}>{body}</div>;
  }
  return (
    <button
      type="button"
      className="flex items-center gap-1.5 px-2 py-1.5 min-w-0 rounded-md hover:bg-muted disabled:hover:bg-transparent disabled:cursor-default"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      {body}
    </button>
  );
}

export function OrderNoteDialog({ initial, saving, error, onSave, onCancel }) {
  const [text, setText] = useState(initial || "");
  return (
    <Modal
      title="Order note"
      onClose={onCancel}
      size="confirm"
      testId="order-note-dialog"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <button type="button" className="h-10 px-4 rounded-md border text-body" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="h-10 px-4 rounded-md bg-action text-action-foreground hover:bg-action/90 text-body font-medium disabled:opacity-50"
            onClick={() => onSave(text.trim())}
            disabled={saving}
            data-testid="order-note-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <textarea
        className="w-full min-h-[96px] rounded-md border p-3 text-body bg-background"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="For the whole table, e.g. birthday cake at 9"
        maxLength={500}
        data-testid="order-note-text"
      />
      {error && <p className="text-meta text-danger mt-2" data-testid="order-note-error">{error}</p>}
    </Modal>
  );
}

export default function OrderContextStrip({ tableName, captainName, guests, note, customerName, readOnly, onGuests, onNote, onCustomer }) {
  return (
    <div className="grid grid-cols-5 gap-0.5 px-2 py-1 border-b bg-card shrink-0" data-testid="order-context-strip">
      <Slot icon={UtensilsCrossed} label="Table" value={tableName || "—"} testId="strip-table" />
      <Slot icon={UserRound} label="Captain" value={captainName || "—"} testId="strip-captain" />
      <Slot icon={UsersRound} label="Guests" value={guests ?? "—"} onClick={onGuests} disabled={readOnly} testId="strip-guests" />
      <Slot icon={MessageSquareText} label="Note" value={note || "Add"} onClick={onNote} disabled={readOnly} testId="strip-note" />
      <Slot icon={UserRoundPlus} label="Customer" value={customerName || "Add"} onClick={onCustomer} disabled={readOnly} testId="strip-customer" />
    </div>
  );
}
